import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { interval, Subscription, switchMap, takeWhile } from 'rxjs';
import { EnvConfigLoaderService } from '@app/shared/services/app/env-config-loader.service';

interface ProvisioningStatus {
  signupRequestId: number;
  signupStatus: string;
  plannedTenantId?: string;
  currentStep?: string;
  progress: number;
  errorMessage?: string;
}

interface Step {
  label: string;
  minProgress: number;
}

@Component({
  selector: 'app-provisioning-status',
  standalone: true,
  imports: [CommonModule, HttpClientModule],
  templateUrl: './provisioning-status.component.html',
  styleUrls: ['./provisioning-status.component.scss']
})
export class ProvisioningStatusComponent implements OnInit, OnDestroy {
  status: ProvisioningStatus | null = null;
  isLoading = true;
  error = '';

  steps: Step[] = [
    { label: 'Registering your company', minProgress: 0 },
    { label: 'Creating your database', minProgress: 15 },
    { label: 'Setting up your workspace', minProgress: 40 },
    { label: 'Creating your account', minProgress: 70 },
    { label: 'Finalizing', minProgress: 90 }
  ];

  private pollSubscription?: Subscription;
  private adminApiUrl: string;
  private env = 'dev';

  constructor(
    private http: HttpClient,
    private route: ActivatedRoute,
    envConfig: EnvConfigLoaderService
  ) {
    this.adminApiUrl = (envConfig as any)?.config?.env?.adminApiUrl || '';
  }

  ngOnInit(): void {
    const requestId = this.route.snapshot.queryParamMap.get('requestId');
    if (!requestId) {
      this.error = 'No request ID provided.';
      this.isLoading = false;
      return;
    }

    this.startPolling(+requestId);
  }

  ngOnDestroy(): void {
    this.pollSubscription?.unsubscribe();
  }

  private startPolling(requestId: number): void {
    // Initial fetch
    this.fetchStatus(requestId);

    // Poll every 5 seconds
    this.pollSubscription = interval(5000).pipe(
      switchMap(() => this.http.get<ProvisioningStatus>(
        `${this.adminApiUrl}/api/${this.env}/signup/${requestId}/provisioning-status`
      )),
      takeWhile(status => status.signupStatus === 'provisioning', true)
    ).subscribe({
      next: status => {
        this.status = status;
        this.isLoading = false;
      },
      error: () => {
        this.error = 'Failed to check provisioning status.';
        this.isLoading = false;
      }
    });
  }

  private fetchStatus(requestId: number): void {
    this.http.get<ProvisioningStatus>(
      `${this.adminApiUrl}/api/${this.env}/signup/${requestId}/provisioning-status`
    ).subscribe({
      next: status => {
        this.status = status;
        this.isLoading = false;
      },
      error: () => {
        this.error = 'Failed to check provisioning status.';
        this.isLoading = false;
      }
    });
  }

  getStepStatus(step: Step): 'completed' | 'active' | 'pending' {
    if (!this.status) return 'pending';
    if (this.status.progress >= step.minProgress + 15) return 'completed';
    if (this.status.progress >= step.minProgress) return 'active';
    return 'pending';
  }

  get isCompleted(): boolean {
    return this.status?.signupStatus === 'completed';
  }

  get isFailed(): boolean {
    return this.status?.signupStatus === 'failed';
  }

  onLogin(): void {
    // Redirect to the main app which will trigger B2C login
    window.location.href = '/';
  }
}
